import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { paymentApi } from '@formosa/shared/api/payment';
import { ApiError } from '@formosa/shared/api/request';
import {
  isBankBindForm,
  isEwalletBindForm,
  resolveBindCurrencyType,
  resolveChannelLabel,
} from '../../constants/withdrawConstants';
import { useTranslation } from '../../i18n/LocaleContext';

export default function WithdrawBindPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const channel = location.state?.channel;

  const bindType = useMemo(() => resolveBindCurrencyType(channel), [channel]);
  const bankForm = useMemo(() => isBankBindForm(channel), [channel]);
  const ewalletForm = useMemo(() => isEwalletBindForm(channel), [channel]);

  const [walletAddress, setWalletAddress] = useState('');
  const [walletName, setWalletName] = useState('');
  const [bankName, setBankName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!channel?.code) {
      navigate('/withdraw/methods', { replace: true });
    }
  }, [channel, navigate]);

  if (!channel?.code) {
    return null;
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError('');
    if (!walletAddress.trim()) {
      setError(t(bankForm ? 'withdraw.bindCardNumberRequired' : 'withdraw.bindAddressRequired'));
      return;
    }
    if (bankForm && !walletName.trim()) {
      setError(t('withdraw.bindAccountNameRequired'));
      return;
    }
    setSubmitting(true);
    try {
      await paymentApi.bindWithdrawAddress({
        channelType: channel.code,
        paymentCurrencyType: bindType,
        walletAddress: walletAddress.trim(),
        walletName: walletName.trim() || undefined,
        bankName: bankName.trim() || undefined,
      });
      navigate('/withdraw/addresses', {
        replace: true,
        state: {
          bound: true,
          from: location.state?.from,
          categoryKey: location.state?.categoryKey,
          categoryLabel: location.state?.categoryLabel,
        },
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('withdraw.bindFailed'));
    } finally {
      setSubmitting(false);
    }
  }

  const canSubmit = walletAddress.trim() && (!bankForm || walletName.trim());

  return (
    <div className="deposit-page withdraw-bind-page">
      <header className="deposit-page__header">
        <button
          type="button"
          className="deposit-page__back"
          aria-label={t('common.back')}
          onClick={() => navigate(-1)}
        >
          ‹
        </button>
        <h1>{t(bankForm ? 'withdraw.bindBankPageTitle' : ewalletForm ? 'withdraw.bindEwalletPageTitle' : 'withdraw.bindPageTitle')}</h1>
        <span className="deposit-page__header-spacer" />
      </header>

      <div className="withdraw-bind-page__notice">
        {t(bankForm ? 'withdraw.bindBankSecurityNotice' : ewalletForm ? 'withdraw.bindEwalletSecurityNotice' : 'withdraw.bindSecurityNotice')}
      </div>

      <form className="withdraw-bind-page__form" onSubmit={handleSubmit}>
        <div className="withdraw-field">
          <div className="withdraw-field__label">{t('withdraw.channel')}</div>
          <div className="withdraw-field__value">{resolveChannelLabel(channel)}</div>
        </div>

        {bankForm && (
          <>
            <div className="withdraw-field">
              <label className="withdraw-field__label" htmlFor="bind-wallet-name">
                {t('withdraw.bindAccountName')}
              </label>
              <input
                id="bind-wallet-name"
                className="withdraw-field__select"
                value={walletName}
                onChange={(e) => setWalletName(e.target.value)}
                placeholder={t('withdraw.bindAccountNamePlaceholder')}
              />
            </div>
            <div className="withdraw-field">
              <label className="withdraw-field__label" htmlFor="bind-bank-name">
                {t('withdraw.bindBankName')}
              </label>
              <input
                id="bind-bank-name"
                className="withdraw-field__select"
                value={bankName}
                onChange={(e) => setBankName(e.target.value)}
                placeholder={t('withdraw.bindBankNamePlaceholder')}
              />
            </div>
          </>
        )}

        <div className="withdraw-field">
          <label className="withdraw-field__label" htmlFor="bind-wallet-address">
            {t(bankForm ? 'withdraw.bindCardNumber' : ewalletForm ? 'withdraw.bindEwalletAccount' : 'withdraw.walletAddress')}
          </label>
          <input
            id="bind-wallet-address"
            className="withdraw-field__select"
            value={walletAddress}
            onChange={(e) => setWalletAddress(e.target.value)}
            placeholder={t(bankForm ? 'withdraw.bindCardNumberPlaceholder' : ewalletForm ? 'withdraw.bindEwalletAccountPlaceholder' : 'withdraw.walletAddressPlaceholder')}
          />
        </div>

        {error && <p className="deposit-page__error">{error}</p>}

        <button
          type="submit"
          className={`withdraw-page__submit ${canSubmit ? 'is-ready' : ''}`}
          disabled={submitting || !canSubmit}
        >
          {submitting ? t('common.loading') : t('withdraw.bindSubmit')}
        </button>
      </form>
    </div>
  );
}
